/**************
 Global
 ***************/

/**************
 Split-theme test attribution
 ***************/

// Stamps the cart with which theme built it, so Shopify Flow can tag the order and the cohort
// stays followable for LTV. `themeTestAttribute` is emitted by layout/theme.liquid (see the
// comment there for why BOTH themes stamp). Guarded so this file still works in any context
// where the global isn't defined.

// Merge our attribute into a cart payload without touching anything else in it.
// Shopify MERGES cart attributes on BOTH /cart/add.js and /cart/update.js — verified on this
// store 2026-08-03 by setting a decoy attribute first and confirming it survived — so this can
// never clobber a key belonging to another app.
function withThemeTestAttribute(formData) {
    if (typeof themeTestAttribute === "undefined" || !themeTestAttribute) {
        return formData;
    }

    return {
        ...formData,
        attributes: {...(formData && formData.attributes), [themeTestAttribute.name]: themeTestAttribute.value}
    };
}

// Re-stamp when the cart disagrees with the theme the shopper is actually on.
//
// Needed because carts outlive themes: the cart cookie survives a cohort switch, and
// /cart/clear.js does NOT clear attributes (verified 2026-08-03 — probe attributes outlived an
// emptied cart), so a stale stamp can persist indefinitely. Re-stamping on mismatch means the
// value always reflects the last theme the shopper actually used before checking out, and it
// also picks up carts created outside this theme's add-to-cart path.
//
// Fires at most one request, only on a genuine mismatch, and only for a cart that has something
// in it — an attribute on an empty cart would mint a cart token for a shopper who never added
// anything.
function reconcileThemeTestAttribute(cart) {
    if (typeof themeTestAttribute === "undefined" || !themeTestAttribute || !cart || !cart.item_count) {
        return Promise.resolve(cart);
    }

    if (cart.attributes && cart.attributes[themeTestAttribute.name] === themeTestAttribute.value) {
        return Promise.resolve(cart);
    }

    return updateCart({attributes: {[themeTestAttribute.name]: themeTestAttribute.value}});
}

//add new product to cart
//let formData = { quantity: 1, id: 794864229, properties: { 'Personalize': 'ZHM' } };
function addProduct(formData) {
    return fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        // stamped here rather than at the 7 addProduct() call sites — this is the single
        // chokepoint every add-to-cart in the theme goes through, so none can be missed
        body: JSON.stringify(withThemeTestAttribute(formData))
    })
        .then(data => data.json());
}

//change an existing product in your cart
//let formData = { quantity: 1, id: 794864229 } or items: [{ quantity: 1, id: 794864229 }]
function changeCart(formData) {
    return fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
        .then(data => data.json());
}

//update cart note or attributes (_ for attributes are hidden and __ are private)
//let formData = { note: 'This is a note about my order', attributes: { 'Gift wrap': 'Yes' } }
//let formData = {updates: { 794864229: 2, 794864230: 3 }}
function updateCart(formData) {
    return fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
        .then(data => data.json());
}

// download product
function downloadProduct(handle) {
    return fetch(`${Shopify.routes.root}products/${handle}.js`)
        .then(data => data.json());
}

// get cart
function getCart() {
    return fetch(`${Shopify.routes.root}cart.js`)
        .then(data => data.json());
}

// supplemental cart item data (selling plans, compare-at prices), cached for the mini cart render
let cartData = {};

// get supplemental cart item data
function getCartData() {
    return fetch("/cart?view=supplemental-data")
        .then(data => data.json());
}

// refresh the in-memory cart and supplemental data in parallel
async function refreshCart() {
    [cart, cartData] = await Promise.all([getCart(), getCartData()]);
    document.dispatchEvent(new CustomEvent("cart:updated"));
}

// print price (515491 => $5,154.91)
function price(amountInCents, removeTrailingZeros = false) {
    // calculate total
    let total = (parseFloat(amountInCents) / 100).toFixed(2).toString();

    // add commas if needed
    total = total.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // remove trailing zeros
    if (removeTrailingZeros) {
        total = total.toString().replace(".00", "");
    }

    // return total
    return `${currency}${total}`;
}

// sleep
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**************
 PDP shipping text — mirror the cart's free-shipping milestone
 ***************/

// the free-shipping threshold (in cents) taken from the mini-cart tracker milestones,
// so the PDP text and the cart tracker can never disagree. Prefers the milestone flagged
// `data-free-shipping`; otherwise falls back to the lowest threshold (usually free shipping).
function freeShippingThreshold() {
    // markets without free shipping have no threshold at all. This guard is what stops the
    // fallback below from latching onto the NEXT-lowest milestone once the shipping one stops
    // rendering there — with a gift tier live, that would have the PDP announcing free shipping
    // at the gift's threshold. layout/theme.liquid publishes the flag, and
    // sections/theme-mini-cart.liquid publishes it again for layouts that do not; anything other
    // than an explicit false means show, matching the fail-open Liquid gate.
    if (window.freeShippingMessagingEnabled === false) {
        return null;
    }
    const milestones = Array.from(document.querySelectorAll(".miniCartHeaderTrackerListItem[data-threshold]"));
    if (milestones.length === 0) {
        return null;
    }
    /*
     Discount milestones are excluded from the candidates BEFORE the fallback runs. Nothing on this
     store ticks is_free_shipping today, so the fallback — lowest threshold wins — is what actually
     identifies the free-shipping tier. Adding a $69 discount milestone below the $79 shipping one
     would therefore have made the PDP announce "Free Shipping $69+". Fixed in the code rather than
     by asking someone to remember a checkbox, and mirrored in theme-mini-cart.liquid.
    */
    const candidates = milestones.filter(m => m.dataset.discountThreshold !== "true");
    if (candidates.length === 0) {
        return null;
    }
    const flagged = candidates.find(m => m.dataset.freeShipping === "true");
    const source = flagged || candidates.reduce((lowest, m) =>
        parseInt(m.dataset.threshold) < parseInt(lowest.dataset.threshold) ? m : lowest
    );
    const threshold = parseInt(source.dataset.threshold);
    return Number.isNaN(threshold) ? null : threshold;
}

// The amount every threshold is measured against: what the customer will ACTUALLY pay, with any
// managed free gift excluded so a gift can never count toward earning itself. Same figure the
// drawer's checkout button shows.
//
// Deliberately NOT cart.items_subtotal_price. That is the sum of final_line_price, which an
// ORDER-level code discount never touches — verified on this store 2026-08-18 — so a 25% code made
// the progress bar read $83.99 on a cart the customer pays $63.00 for, promising free shipping
// that had not been earned. Every consumer of a threshold reads this one function so the bar, the
// PDP shipping line, the free-gift reconcile and the footer can never disagree.
function qualifyingTotal() {
    if (typeof cart === "undefined" || !cart) {
        return 0;
    }

    let giftTotal = 0;
    (cart.items || []).forEach(item => {
        if (item.properties && item.properties._free_gift) {
            giftTotal += item.final_line_price;
        }
    });

    return Math.max(0, (cart.total_price || 0) - giftTotal);
}

/*
 The basis a DISCOUNT threshold is measured against: the pre-discount subtotal of the lines a
 discount could apply to.

 Deliberately NOT qualifyingTotal(). That is what the customer pays AFTER discounts, which is
 right for free shipping and a free gift, and WRONG here in a way that is visible: a $72 cart
 clears a $69 discount threshold, the discount lands, the payable total drops to ~$58 — back under
 the threshold — and the bar flips between "unlocked" and "add $11 more" on every render. Shopify
 evaluates a discount's minimum against the pre-discount subtotal, so this mirrors that.

 The free gift is excluded for the same reason it is excluded from qualifyingTotal(): a gift must
 never count toward earning itself.

 A scoped code counts only the lines it covers toward its minimum (a $39.98 grip beside a $129.99
 MCON Open Box did not unlock SAVE25ON69's $69, 2026-09-22). data-dp-scope is the active offer's
 collection, and window.OhSnapDiscountScopes lists its products (snippets/discount-rules.liquid);
 when either is missing every line counts. Mirrors subtotalOf() in assets/theme-discount.js.
*/
function preDiscountSubtotal() {
    if (typeof cart === "undefined" || !cart) {
        return 0;
    }
    const scope = document.documentElement.dataset.dpScope;
    const covered = scope && window.OhSnapDiscountScopes ? window.OhSnapDiscountScopes[scope] : null;
    let total = 0;
    (cart.items || []).forEach(item => {
        if (item.properties && item.properties._free_gift) {
            return;
        }
        if (Array.isArray(covered) && covered.indexOf(item.product_id) === -1) {
            return;
        }
        total += item.original_line_price;
    });
    return total;
}

// swap PDP shipping text between the locked and unlocked messages based on the current cart.
// uses qualifyingTotal(), the same figure as theme-mini-cart.js renderTracker(), so the two match.
// the setting reads "<shipping message> | <standing callouts>" (e.g. "Free Shipping $79+ | 1 Year
// Extended Warranty"). only the leading shipping message swaps — everything from the first "|" on
// is still true once shipping unlocks, so it is carried across rather than overwritten.
function renderShippingText() {
    const elements = document.querySelectorAll(".productV1ContentShipping[data-unlocked-text]");
    if (elements.length === 0) {
        return;
    }
    const threshold = freeShippingThreshold();
    const unlocked = threshold !== null && qualifyingTotal() >= threshold;
    elements.forEach(el => {
        const locked = el.dataset.lockedText;
        const separator = locked.indexOf("|");
        const callouts = separator === -1 ? "" : locked.slice(separator).trim();
        const messageEl = el.querySelector(".productV1ContentShippingMessage");

        if (messageEl) {
            const lockedMessage = separator === -1 ? locked : locked.slice(0, separator).trim();
            const message = unlocked ? el.dataset.unlockedText : lockedMessage;
            if (messageEl.textContent !== message) {
                messageEl.textContent = message;
            }
        } else {
            // pre-snippet markup (a section this snippet hasn't been rolled out to): whole line
            const text = unlocked
                ? [el.dataset.unlockedText, callouts].filter(Boolean).join(" ")
                : locked;
            if (el.textContent !== text) {
                el.textContent = text;
            }
        }

        el.classList.toggle("productV1ContentShippingUnlocked", unlocked);
    });
}

/**************
 Warranty tooltip
 ***************/

// Winning state of the 2026-08 PDP + cart warranty A/B test, rolled out to everyone.
//
// The phrase is UPGRADED here rather than rendered as a button in Liquid. That was the tested
// shape and it stays: snippets/pdp-shipping-text.liquid emits plain text plus a <template>
// holding the tooltip copy, and this file turns the two into a toggle + popover. Keeping the
// upgrade in JS is what let the test ship a control cohort with no stray button in the
// accessibility tree; now that everyone gets it, the same code path just runs unconditionally.
//
// Consequence worth knowing: theme-global.js is deferred, so the phrase paints as plain text
// for one frame before the icon appears. Moving the markup into Liquid would remove that, but
// it is a rewrite of markup that was never tested in that form — treat it as a follow-up.

const WARRANTY_TOOLTIP_ICON = '<svg class="pdpWarrantyTooltipToggleIcon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 .833A7.167 7.167 0 1 0 8 15.167 7.167 7.167 0 0 0 8 .833Zm0 13.334A6.167 6.167 0 1 1 8 1.833a6.167 6.167 0 0 1 0 12.334ZM7.25 7.25h1.5v4.583h-1.5V7.25Zm0-3h1.5v1.5h-1.5v-1.5Z"/></svg>';

// turn the plain phrase into a toggle button + popover. Idempotent: safe to call again after
// anything re-renders the shipping line.
function applyWarrantyTooltip() {
    document.querySelectorAll(".pdpWarrantyTooltipPhrase").forEach(phrase => {
        if (phrase.dataset.upgraded) {
            return;
        }
        const line = phrase.closest(".productV1ContentShipping");
        const template = line && line.querySelector(".pdpWarrantyTooltipContent");
        if (!template) {
            return;
        }

        phrase.dataset.upgraded = "1";
        phrase.innerHTML = `<button class="pdpWarrantyTooltipToggle" type="button" aria-expanded="false"><span>${phrase.textContent}</span>${WARRANTY_TOOLTIP_ICON}</button>`;

        const popover = document.createElement("div");
        popover.className = "pdpWarrantyTooltipPopover";
        popover.appendChild(template.content.cloneNode(true));
        line.appendChild(popover);
    });
}

function closePdpWarrantyTooltips() {
    document.querySelectorAll(".pdpWarrantyTooltipPopover.active").forEach(popover => {
        popover.classList.remove("active");
        const toggle = popover.closest(".productV1ContentShipping").querySelector(".pdpWarrantyTooltipToggle");
        if (toggle) {
            toggle.setAttribute("aria-expanded", "false");
        }
    });
}

// the whole phrase and the icon are one button, so a click on either opens the tooltip.
// delegated from document because the button is created after Shoplift resolves.
document.addEventListener("click", e => {
    const toggle = e.target.closest(".pdpWarrantyTooltipToggle");
    if (!toggle) {
        return;
    }
    const line = toggle.closest(".productV1ContentShipping");
    const popover = line.querySelector(".pdpWarrantyTooltipPopover");
    const willOpen = !popover.classList.contains("active");

    closePdpWarrantyTooltips();

    if (willOpen) {
        popover.classList.add("active");
        toggle.setAttribute("aria-expanded", "true");
    }
});

document.addEventListener("mousedown", e => {
    if (e.target.closest(".productV1ContentShipping")) {
        return;
    }
    closePdpWarrantyTooltips();
});

document.addEventListener("keydown", e => {
    if (e.key === "Escape") {
        closePdpWarrantyTooltips();
    }
});

// every layout loads this file with `defer`, so the shipping line is already parsed by the time
// we get here. The readyState guard matches renderShippingText below and costs nothing.
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyWarrantyTooltip);
} else {
    applyWarrantyTooltip();
}

// run on load, then react to every cart change (ATC, cart-drawer edits)
document.addEventListener("cart:updated", renderShippingText);
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderShippingText);
} else {
    renderShippingText();
}

/**************
 Design mode open mini cart
 ***************/
if (Shopify.designMode) {
    // if a user clicks on the mini cart
    document.addEventListener('shopify:section:select', function (event) {
        // open mini cart
        if (event.detail.sectionId === 'theme-mini-cart') {
            openCart();
        }

        // if they deselect the mini cart
        if (event.detail.sectionId !== 'theme-mini-cart') {
            closeCart();
        }
    });
}

// scroll to url hash
const maltertechParams = new URLSearchParams(window.location.search);

if (maltertechParams.get("scroll_to_class")) {
    const element = document.querySelector(`.${maltertechParams.get("scroll_to_class")}`);
    if (element) {
        window.scrollTo({
            behavior: 'smooth',
            top: element.offsetTop - 120,
        })
    }
}

if (maltertechParams.get("scroll_to_id")) {
    const element = document.querySelector(`#${maltertechParams.get("scroll_to_id")}`);
    if (element) {
        window.scrollTo({
            behavior: 'smooth',
            top: element.offsetTop - 120,
        })
    }
}

// Function to send back in stock subscription to Klaviyo
async function sendKlaviyoBackInStockRequest(emailAddress, variantID) {
    await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${await klaviyo.account()}`, {
        method: 'POST',
        headers: {
            accept: 'application/vnd.api+json',
            revision: '2024-10-15',
            'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify({
            data: {
                type: "back-in-stock-subscription",
                attributes: {
                    channels: ["EMAIL"],
                    profile: {
                        data: {
                            type: "profile",
                            attributes: {
                                email: emailAddress,
                            }
                        }
                    }
                },
                relationships: {
                    variant: {
                        data: {
                            type: "catalog-variant",
                            id: `$shopify:::$default:::${variantID}`,
                        }
                    }
                }
            }
        })
    })
}

// upsell swatch click sets that variant to active
document.addEventListener("click", function (e) {
    const swatch = e.target.closest(".upsellItemSwatchesItem");
    if (!swatch) {
        return;
    }

    const card = swatch.closest(".upsellItem");
    if (!card) {
        return;
    }

    // move the active ring to the clicked swatch
    card.querySelectorAll(".upsellItemSwatchesItem.active").forEach(element => element.classList.remove("active"));
    swatch.classList.add("active");

    // retarget the add button at the selected variant
    const addButton = card.querySelector(".upsellItemActionsAdd");
    if (addButton && swatch.dataset.variant) {
        addButton.dataset.variant = swatch.dataset.variant;
    }

    // swap the card image to the variant image when one exists
    const image = card.querySelector(".upsellItemImage");
    if (image && swatch.dataset.imageSrc) {
        image.src = swatch.dataset.imageSrc;
        image.srcset = swatch.dataset.imageSrcset;
        image.alt = swatch.dataset.imageAlt;
    }
});

// collection swatch click sets that variant to active (non-compact cards only)
document.addEventListener("click", function (e) {
    const swatch = e.target.closest(".collectionItemSwatchesItem");
    if (!swatch) {
        return;
    }

    const card = swatch.closest(".collectionItem:not(.compact)");
    if (!card) {
        return;
    }

    // the whole card is a link, so don't navigate when picking a color
    e.preventDefault();

    // move the active ring to the clicked swatch
    card.querySelectorAll(".collectionItemSwatchesItem.active").forEach(element => element.classList.remove("active"));
    swatch.classList.add("active");

    // retarget the add button at the selected variant
    const addButton = card.querySelector(".collectionItemAdd");
    if (addButton && swatch.dataset.variant) {
        addButton.dataset.variant = swatch.dataset.variant;
    }

    // swap the card image to the variant image when one exists
    const image = card.querySelector(".collectionItemImage");
    if (image && swatch.dataset.imageSrc) {
        image.src = swatch.dataset.imageSrc;
        image.srcset = swatch.dataset.imageSrcset;
        image.alt = swatch.dataset.imageAlt;
    }
});

// card ATC buttons- .collectionItemAdd lives inside a link, so stop the click from navigating
document.addEventListener("click", async function (e) {
    const button = e.target.closest(".collectionItemAdd, .upsellItemActionsAdd");
    if (!button) {
        return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (button.disabled) {
        return;
    }

    const variantID = parseInt(button.dataset.variant);
    if (!variantID) {
        return;
    }

    button.disabled = true;
    button.classList.add("loading");

    try {
        await addProduct({items: [{quantity: 1, id: variantID}]});

        if (typeof openCart === "function") {
            await openCart();
        }
    } finally {
        button.disabled = false;
        button.classList.remove("loading");
    }
});